import { defineStore } from 'pinia'
import { getLocalState, setLocalState } from './helper'
import { getListClassroom } from '@/api/classroom'
import { COMMON } from '@/constants'

export const useClassroomStore = defineStore('classroom-store', {
  state: (): Classroom.ClassroomState => getLocalState(),

  getters: {
    getClassrooms: state => state.classrooms,
    getLoading: state => state.loading,
    getCurrentClassroom: state => state.currentClassroom,
    getEditorTemp: state => state.editorTemp,
    getPagination: state => state.pagination,
    getClassroomById: state => (id: string | number) => {
      return id ? state.classrooms.find(classroom => classroom.id === Number(id)) : {}
    },
    getEditorAgent: state => state.editorAgent,
    getFromAgent: state => state.isFromAgent,
    getSourceAgent: state => state.sourceAgent,
  },

  actions: {
    setLoading(loading: boolean) {
      this.loading = loading
      this.recordState()
    },

    setClassrooms(classrooms: any[]) {
      this.classrooms = classrooms
      this.recordState()
    },

    setCurrentClassroom(classroom: any) {
      this.currentClassroom = classroom
      this.recordState()
    },

    setEditorTemp(payload: Classroom.ClassroomState['editorTemp']) {
      this.editorTemp = payload
      this.recordState()
    },

    clearEditorTemp() {
      this.editorTemp = null
      this.recordState()
    },

    setEditorAgent(agent: any) {
      this.editorAgent = agent
      this.recordState()
    },
    clearEditorAgent() {
      this.editorAgent = null
      this.recordState()
    },
    setFromAgent(v: any) {
      this.isFromAgent = v
      this.recordState()
    },
    clearFromAgent() {
      this.isFromAgent = false
      this.recordState()
    },
    setSourceAgent(v: any) {
      this.sourceAgent = { ...v }
      this.recordState()
    },
    clearSourceAgent() {
      this.sourceAgent = {
        sourceScreen: null,
        sourceData: {},
      }
      this.recordState()
    },

    setPagination(pagination: Classroom.ClassroomState['pagination']) {
      this.pagination = pagination
      this.recordState()
    },

    addClassroom(classroom: any) {
      this.classrooms.unshift(classroom)
      this.recordState()
    },

    updateClassroom(classroom: any) {
      const index = this.classrooms.findIndex(item => item.id === classroom.id)
      if (index !== -1) {
        this.classrooms[index] = { ...this.classrooms[index], ...classroom }
        this.recordState()
      }
    },

    removeClassroom(classroomId: string | number) {
      const index = this.classrooms.findIndex(item => item.id === classroomId)
      if (index !== -1) {
        this.classrooms.splice(index, 1)
        this.recordState()
      }
    },

    async fetchClassrooms(workspaceId: string | number, keyword?: any, pagination?: any) {
      try {
        this.setLoading(true)
        const params = {
          keyword: keyword || null,
          page: pagination?.page || null,
          page_size: pagination?.pageSize || null,
          sort: false,
          sort_by: COMMON.SORT_BY.CREATION_DATE,
          workspace_id: workspaceId,
        }

        const { data } = await getListClassroom(params)
        if (data?.data?.items) {
          this.setClassrooms(data.data.items || [])

          // Cập nhật pagination trong store
          if (pagination && data?.data?.pagination) {
            this.setPagination({
              page: data.data.pagination.page || 1,
              pageSize: data.data.pagination.page_size || 10,
              itemCount: data.data.pagination.total || 0,
              pageCount: data.data.pagination.total_pages || 0,
              pageSizes: pagination?.pageSizes || [],
            })
          }
        }
        else {
          this.setClassrooms([])
          this.setPagination({
            page: 1,
            pageSize: 10,
            itemCount: 0,
            pageCount: 0,
            pageSizes: [],
          })
        }

        return data
      }
      catch (error) {
        this.setClassrooms([])
        this.setPagination({
          page: 1,
          pageSize: 10,
          itemCount: 0,
          pageCount: 0,
          pageSizes: [],
        })
        throw error
      }
      finally {
        this.setLoading(false)
      }
    },

    clearClassrooms() {
      this.classrooms = []
      this.currentClassroom = null
      this.recordState()
    },

    recordState() {
      setLocalState(this.$state)
    },
  },
})
